import { expandAllParentsThenScroll } from '@atlaskit/editor-common/block-menu/scroll-to-block-utils';

import { expVal } from '@confluence/feature-experiments';
import { getHeadingIdsToElements } from '@confluence/editor-heading-links/entry-points/headingLinksFromElements.ts';

/**
 * Scrolls the heading element into view within the editor's scroll container.
 * Calculates the scroll position relative to the container and smoothly scrolls to it.
 */
const scrollHeadingIntoView = (headingElement: Element): void => {
	// This function is only called in browser-execution contexts (directly from useEffect or via
	// user interaction), so document is guaranteed to exist.
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	const scrollContainer = document.getElementsByClassName('fabric-editor-popup-scroll-parent')?.[0];

	const headingTop = headingElement.getBoundingClientRect()?.top;
	const scrollContainerTop = scrollContainer?.getBoundingClientRect()?.top;
	const offset = scrollContainer?.scrollTop;

	if (headingTop === undefined || scrollContainerTop === undefined || offset === undefined) {
		return;
	}

	// Subtract the scroll container position from the heading position to get the scroll distance
	// and add the offset to account for current scroll position.
	const scrollToY = headingTop - scrollContainerTop + offset;

	scrollContainer?.scrollTo({
		left: 0,
		top: scrollToY,
		behavior: 'smooth',
	});
};

/**
 * Resolves a generated editor heading ID and scrolls its DOM element into view.
 *
 * Returns a cleanup callback when scrolling is deferred while collapsed parents expand.
 */
export const scrollEditorHeadingIntoView = (headingId: string): (() => void) | null => {
	const headingElement = getHeadingIdsToElements().get(headingId);

	if (!headingElement) {
		return null;
	}

	if (
		headingElement instanceof HTMLElement &&
		expVal('platform_editor_fix_copy_link_heading_in_expand', 'isEnabled', false)
	) {
		return expandAllParentsThenScroll(headingElement, 0, (element) => {
			scrollHeadingIntoView(element);
		});
	}

	scrollHeadingIntoView(headingElement);
	return null;
};
